/**
 * useSystemAccess — Verknüpfung zu User-Account, Rollen & Berechtigungen.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { employeesAPI, permissionsAPI, rolesAPI, usersAPI } from '../../lib/api';
import { emptyPermissions as emptyPermissionsTemplate } from './helpers';

export function useSystemAccess({ employeeId, isEdit }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [linkedUser, setLinkedUser] = useState(null);
  const [linkingUser, setLinkingUser] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [creatingAccess, setCreatingAccess] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [userPermissions, setUserPermissions] = useState({ ...emptyPermissionsTemplate });

  const loadUsersAndRoles = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([usersAPI.getAll(), rolesAPI.getAll()]);
      setUsers(usersRes.data || []);
      setRoles(rolesRes.data || []);
    } catch (e) {
      console.error('Failed to load users/roles', e);
    }
  }, []);

  const loadUserPermissions = useCallback(async (userId) => {
    try {
      const response = await permissionsAPI.getOne(userId);
      if (response.data) {
        setUserPermissions({
          role_id: response.data.role_id || null,
          scope_type: response.data.scope_type || 'own',
          branch_ids: response.data.branch_ids || [],
          location_ids: response.data.location_ids || [],
          all_branches: response.data.all_branches || false,
          all_locations: response.data.all_locations || false,
          permissions: response.data.permissions || {},
        });
      }
    } catch (e) {
      console.error('Failed to load permissions', e);
    }
  }, []);

  const loadLinkedUser = useCallback(async () => {
    if (!employeeId) return;
    try {
      const response = await employeesAPI.getLinkedUser(employeeId);
      if (response.data.linked) {
        setLinkedUser(response.data.user);
        loadUserPermissions(response.data.user.id);
      }
    } catch (e) {
      console.error('Failed to load linked user:', e);
    }
  }, [employeeId, loadUserPermissions]);

  useEffect(() => {
    loadUsersAndRoles();
    if (isEdit) loadLinkedUser();
  }, [employeeId, isEdit, loadUsersAndRoles, loadLinkedUser]);

  const generatePassword = () => {
    const chars = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) password += chars.charAt(Math.floor(Math.random() * chars.length));
    setNewPassword(password);
  };

  const handleRoleSelect = (roleId, defaultLocationIds = []) => {
    const role = roles.find((r) => r.id === roleId);
    if (role) {
      const scope = role.scope_type || 'own';
      setUserPermissions((prev) => {
        // Bei Standort-Rollen (z. B. Haustechniker) den Arbeitsort vorschlagen,
        // wenn noch keine Standorte gewählt sind.
        const preselect = scope === 'location' && (prev.location_ids || []).length === 0
          ? (defaultLocationIds || []).filter(Boolean)
          : prev.location_ids;
        return {
          ...prev,
          role_id: roleId,
          scope_type: scope,
          permissions: role.permissions || {},
          location_ids: preselect,
        };
      });
    }
  };

  const handlePermissionChange = (field, value) => {
    setUserPermissions((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateSystemAccess = async (businessEmail) => {
    const useAutoGenerate = !newPassword;
    if (!useAutoGenerate && newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    if (!businessEmail) {
      toast.error('Mitarbeiter benoetigt eine dienstliche E-Mail-Adresse (unter Beschaeftigung)');
      return;
    }
    setCreatingAccess(true);
    try {
      const payload = {
        role_id: userPermissions.role_id,
        scope_type: userPermissions.scope_type,
        branch_ids: userPermissions.branch_ids,
        location_ids: userPermissions.location_ids,
      };
      if (useAutoGenerate) payload.auto_generate_password = true;
      else payload.password = newPassword;

      const res = await employeesAPI.createSystemAccess(employeeId, payload);
      const generatedPw = res.data?.generated_password;
      if (generatedPw) {
        toast.success(`Systemzugang erstellt! Temporaeres Passwort: ${generatedPw}`, { duration: 15000 });
        setNewPassword(generatedPw);
        setShowNewPassword(true);
      } else {
        toast.success('Systemzugang erstellt! Der Mitarbeiter muss beim ersten Login ein neues Passwort und einen PIN setzen.');
        setNewPassword('');
      }
      loadLinkedUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreatingAccess(false);
    }
  };

  const handleRemoveSystemAccess = async () => {
    if (!window.confirm('Systemzugang wirklich entfernen? Der Benutzer kann sich dann nicht mehr anmelden.')) return;
    setLinkingUser(true);
    try {
      await employeesAPI.removeSystemAccess(employeeId);
      toast.success('Systemzugang entfernt');
      setLinkedUser(null);
      setUserPermissions({ ...emptyPermissionsTemplate });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen');
    } finally {
      setLinkingUser(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!linkedUser) return;
    setSavingPermissions(true);
    try {
      await permissionsAPI.update(linkedUser.id, {
        user_id: linkedUser.id,
        role_id: userPermissions.role_id,
        scope_type: userPermissions.scope_type,
        branch_ids: userPermissions.branch_ids,
        location_ids: userPermissions.location_ids,
        all_branches: userPermissions.scope_type === 'all',
        all_locations: userPermissions.scope_type === 'all',
      });
      toast.success('Berechtigungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Rechte');
    } finally {
      setSavingPermissions(false);
    }
  };

  return {
    users, roles, linkedUser, linkingUser, savingPermissions, creatingAccess,
    newPassword, setNewPassword, showNewPassword, setShowNewPassword,
    userPermissions, setUserPermissions,
    generatePassword,
    handleRoleSelect, handlePermissionChange,
    handleCreateSystemAccess, handleRemoveSystemAccess, handleSavePermissions,
  };
}
